import React from 'react';
import gsap from 'gsap';
import { ANIMATION_FUNCTIONS } from '../../../utils/textStylePresets';
import { isTypewriterCaptionStyle } from '../../../utils/captionStyleUtils';

const hexToRgb = (hexColor, fallback = [215, 220, 232]) => {
  if (typeof hexColor !== 'string') {
    return `${fallback[0]}, ${fallback[1]}, ${fallback[2]}`;
  }

  const normalized = hexColor.trim().replace(/^#/, '');
  const fullHex = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  if (!/^[0-9a-fA-F]{6}$/.test(fullHex)) {
    return `${fallback[0]}, ${fallback[1]}, ${fallback[2]}`;
  }

  const value = parseInt(fullHex, 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `${r}, ${g}, ${b}`;
};

const CaptionPresetButton = ({ preset, updateStyle }) => {
  const containerRef = React.useRef(null);
  const previewTextRef = React.useRef(null);
  const blurLayerRef = React.useRef(null);
  const activeBlurRef = React.useRef(null);
  const wordRefs = React.useRef([]);
  const typewriterTextRef = React.useRef(null);
  const typewriterCursorRef = React.useRef(null);
  const tlRef = React.useRef(null);
  const blurTrailNodesRef = React.useRef([]);
  const lastBlurAnchorRef = React.useRef(null);
  const [isInView, setIsInView] = React.useState(false);

  const wordTimeline = React.useMemo(
    () => [
      { text: 'The', start: 0, end: 260, phraseIdx: 0 },
      { text: 'quick', start: 260, end: 520, phraseIdx: 0 },
      { text: 'brown', start: 520, end: 780, phraseIdx: 1 },
      { text: 'fox', start: 780, end: 1040, phraseIdx: 1 },
    ],
    []
  );

  const allWords = React.useMemo(() => wordTimeline.map((word) => word.text), [wordTimeline]);
  const previewPhraseText = React.useMemo(() => allWords.join(' '), [allWords]);
  const isTypewriterPreset = React.useMemo(() => isTypewriterCaptionStyle(preset), [preset]);
  const phrase1Length = React.useMemo(
    () => wordTimeline.filter((word) => word.phraseIdx === 0).length,
    [wordTimeline]
  );

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    const clearBlurPreview = () => {
      const activeBlurElement = activeBlurRef.current;
      if (activeBlurElement) {
        gsap.killTweensOf(activeBlurElement);
        activeBlurElement.style.opacity = '0';
        activeBlurElement.style.width = '0px';
        activeBlurElement.style.height = '0px';
        activeBlurElement.style.background = 'transparent';
        activeBlurElement.style.filter = 'none';
        activeBlurElement.style.transform = 'translate(-9999px, -9999px)';
      }

      blurTrailNodesRef.current.forEach((node) => {
        if (!node) return;
        gsap.killTweensOf(node);
        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      });
      blurTrailNodesRef.current = [];
      lastBlurAnchorRef.current = null;
    };

    if (!isInView) {
      if (tlRef.current) {
        tlRef.current.kill();
        tlRef.current = null;
      }

      clearBlurPreview();

      if (typewriterTextRef.current) {
        gsap.killTweensOf(typewriterTextRef.current);
        typewriterTextRef.current.textContent = '';
        typewriterTextRef.current.style.backgroundColor = 'transparent';
        typewriterTextRef.current.style.padding = '0';
        typewriterTextRef.current.style.borderRadius = '0';
        typewriterTextRef.current.style.display = 'inline';
        typewriterTextRef.current.style.lineHeight = '';
      }
      if (typewriterCursorRef.current) {
        gsap.killTweensOf(typewriterCursorRef.current);
        typewriterCursorRef.current.textContent = '';
        typewriterCursorRef.current.style.marginLeft = '0';
      }

      wordRefs.current.forEach((element) => {
        if (!element) return;
        gsap.killTweensOf(element);
        gsap.set(element, { clearProps: 'all', opacity: 0 });
        element.style.backgroundColor = 'transparent';
        element.style.borderRadius = '0';
        element.style.padding = '0';
      });
      return undefined;
    }

    const usesWordContainerPreview = Boolean(
      preset.displayMode ||
      preset.highlightColor ||
      preset.highlightBg ||
      preset.activeWordBlur
    );
    const displayMode = usesWordContainerPreview
      ? (preset.displayMode || 'karaoke')
      : 'standard';
    const wordAnimation = preset.wordAnimation || preset.animation || 'fadeIn';
    const highlightColor = preset.highlightColor;
    const highlightBackground = preset.highlightBg;
    const parsedHighlightStartPhrase = parseInt(preset.highlightFromPhraseIndex, 10);
    const highlightFromPhraseIndex = Number.isFinite(parsedHighlightStartPhrase)
      ? parsedHighlightStartPhrase
      : 0;
    const revealOpacityMode = preset.revealOpacityMode || null;
    const parsedInactiveOpacity = Number(preset.inactiveOpacity);
    const inactiveOpacity = Number.isFinite(parsedInactiveOpacity)
      ? Math.max(0, Math.min(1, parsedInactiveOpacity))
      : 0.28;
    const baseColor = preset.color || '#ffffff';
    const pastColor = preset.pastPhraseColor || '#ffffff';
    const forceSinglePhrasePreview = (
      preset.key === 'custom_yellow_halo' ||
      preset.key === 'custom_pale_reveal'
    );
    const previewFontSize = 12;
    const sourceFontSize = Number.isFinite(Number(preset.fontSize))
      ? Math.max(1, Number(preset.fontSize))
      : previewFontSize;
    const previewScale = Math.max(0.2, Math.min(1, previewFontSize / sourceFontSize));
    const previewBlurBoost = 1.5;
    const activeWordBlurEnabled = Boolean(preset.activeWordBlur);
    const parsedActiveWordBlurRadius = Number(preset.activeWordBlurRadius);
    const activeWordBlurRadius = Number.isFinite(parsedActiveWordBlurRadius)
      ? Math.max(6, parsedActiveWordBlurRadius * previewScale * previewBlurBoost)
      : Math.max(14, sourceFontSize * 0.82 * previewScale * previewBlurBoost);
    const parsedActiveWordBlurTrailMs = Number(preset.activeWordBlurTrailMs);
    const activeWordBlurTrailMs = Number.isFinite(parsedActiveWordBlurTrailMs)
      ? Math.max(80, Math.min(2200, parsedActiveWordBlurTrailMs))
      : 460;
    const parsedActiveWordBlurOpacity = Number(preset.activeWordBlurOpacity);
    const activeWordBlurOpacity = Number.isFinite(parsedActiveWordBlurOpacity)
      ? Math.max(0, Math.min(1, parsedActiveWordBlurOpacity))
      : 0.94;
    const parsedActiveWordBlurOffsetX = Number(preset.activeWordBlurOffsetX);
    const activeWordBlurOffsetX = Number.isFinite(parsedActiveWordBlurOffsetX)
      ? parsedActiveWordBlurOffsetX * previewScale
      : 8 * previewScale;
    const parsedActiveWordBlurOffsetY = Number(preset.activeWordBlurOffsetY);
    const activeWordBlurOffsetY = Number.isFinite(parsedActiveWordBlurOffsetY)
      ? parsedActiveWordBlurOffsetY * previewScale
      : 2 * previewScale;
    const parsedActiveWordBlurWordAlpha = Number(preset.activeWordBlurWordAlpha);
    const activeWordBlurWordAlpha = Number.isFinite(parsedActiveWordBlurWordAlpha)
      ? Math.max(0, Math.min(1, parsedActiveWordBlurWordAlpha))
      : 0;
    const parsedActiveWordBlurXRatio = Number(preset.activeWordBlurXRatio);
    const activeWordBlurXRatio = Number.isFinite(parsedActiveWordBlurXRatio)
      ? Math.max(0, Math.min(1.3, parsedActiveWordBlurXRatio))
      : 0.74;
    const activeWordBlurColorRgb = hexToRgb(preset.activeWordBlurColor || baseColor);

    const setBlurNodeStyle = (node, anchor, opacityValue) => {
      if (!node || !anchor) return;
      const boundedOpacity = Math.max(0, Math.min(1, opacityValue));
      const outerRadius = Math.max(8, anchor.radius * 2.2);
      const diameter = outerRadius * 2;
      node.style.position = 'absolute';
      node.style.display = 'block';
      node.style.pointerEvents = 'none';
      node.style.left = `${anchor.x - outerRadius}px`;
      node.style.top = `${anchor.y - outerRadius}px`;
      node.style.width = `${diameter}px`;
      node.style.height = `${diameter}px`;
      node.style.borderRadius = '999px';
      node.style.transformOrigin = 'center';
      node.style.willChange = 'transform, opacity, filter';
      node.style.background = `radial-gradient(circle, rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.96)}) 0%, rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.94)}) 18%, rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.6)}) 33%, rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.46)}) 46%, rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.34)}) 58%, rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.25)}) 70%, rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.19)}) 80%, rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.14)}) 88%, rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.1)}) 94%, rgba(${activeWordBlurColorRgb}, 0) 100%)`;
      node.style.filter = `blur(${Math.max(2.5, anchor.radius * 0.24)}px)`;
      node.style.boxShadow = `0 0 ${Math.max(6, anchor.radius * 0.92)}px rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.58)}), 0 0 ${Math.max(12, anchor.radius * 1.6)}px rgba(${activeWordBlurColorRgb}, ${Math.max(0, boundedOpacity * 0.34)})`;
    };

    const moveActiveBlurToWord = (wordIndex) => {
      if (!activeWordBlurEnabled) return;

      const blurLayerElement = blurLayerRef.current;
      const activeBlurElement = activeBlurRef.current;
      const wordElement = wordRefs.current[wordIndex];
      if (!blurLayerElement || !activeBlurElement || !wordElement) return;

      const blurLayerRect = blurLayerElement.getBoundingClientRect();
      const wordRect = wordElement.getBoundingClientRect();
      if (wordRect.width <= 0 || wordRect.height <= 0) return;

      const anchor = {
        x: (wordRect.left - blurLayerRect.left) + (wordRect.width * activeWordBlurXRatio) + activeWordBlurOffsetX,
        y: (wordRect.top - blurLayerRect.top) + (wordRect.height * 0.56) + activeWordBlurOffsetY,
        radius: activeWordBlurRadius + (wordRect.height * 0.04),
      };

      const previousAnchor = lastBlurAnchorRef.current;
      if (
        previousAnchor &&
        (Math.abs(previousAnchor.x - anchor.x) > 0.3 || Math.abs(previousAnchor.y - anchor.y) > 0.3)
      ) {
        const trailNode = document.createElement('span');
        setBlurNodeStyle(trailNode, previousAnchor, activeWordBlurOpacity * 0.72);
        blurLayerElement.appendChild(trailNode);
        blurTrailNodesRef.current.push(trailNode);

        gsap.fromTo(
          trailNode,
          { opacity: Math.max(0.1, activeWordBlurOpacity * 0.72), scale: 1 },
          {
            opacity: 0,
            scale: 1.22,
            duration: Math.max(0.12, activeWordBlurTrailMs / 1000),
            ease: 'power2.out',
            onComplete: () => {
              const trailNodes = blurTrailNodesRef.current;
              const trailIndex = trailNodes.indexOf(trailNode);
              if (trailIndex >= 0) {
                trailNodes.splice(trailIndex, 1);
              }
              if (trailNode.parentNode) {
                trailNode.parentNode.removeChild(trailNode);
              }
            },
          }
        );
      }

      gsap.killTweensOf(activeBlurElement);
      setBlurNodeStyle(activeBlurElement, anchor, activeWordBlurOpacity);
      gsap.fromTo(
        activeBlurElement,
        { opacity: Math.max(0.12, activeWordBlurOpacity * 0.52), scale: 0.94 },
        { opacity: activeWordBlurOpacity, scale: 1, duration: 0.12, ease: 'power2.out' }
      );
      lastBlurAnchorRef.current = anchor;
    };

    clearBlurPreview();

    if (isTypewriterPreset) {
      const typewriterElement = typewriterTextRef.current;
      const cursorElement = typewriterCursorRef.current;
      if (!typewriterElement || !cursorElement) return undefined;

      const characterCount = previewPhraseText.length;
      const progress = { value: 0 };
      const cursorGlyph = preset.typewriterCursor === 'block' ? '\u2589' : '|';
      const hasTypewriterBackground = Boolean(preset.typewriterBackgroundColor);
      const rawPaddingX = Number.isFinite(Number(preset.typewriterBackgroundPaddingX))
        ? Math.max(0, Number(preset.typewriterBackgroundPaddingX))
        : 8;
      const rawRadius = Number.isFinite(Number(preset.typewriterBackgroundRadius))
        ? Math.max(0, Number(preset.typewriterBackgroundRadius))
        : 4;
      const typewriterPaddingX = hasTypewriterBackground
        ? Math.max(1, Math.round(rawPaddingX * previewScale))
        : 0;
      const typewriterPaddingY = 0;
      const typewriterRadius = hasTypewriterBackground
        ? Math.max(1, Math.round(rawRadius * previewScale))
        : 0;

      typewriterElement.style.color = baseColor;
      typewriterElement.style.backgroundColor = hasTypewriterBackground
        ? preset.typewriterBackgroundColor
        : 'transparent';
      typewriterElement.style.padding = hasTypewriterBackground
        ? `${typewriterPaddingY}px ${typewriterPaddingX}px`
        : '0';
      typewriterElement.style.borderRadius = hasTypewriterBackground
        ? `${typewriterRadius}px`
        : '0';
      typewriterElement.style.display = 'inline';
      typewriterElement.style.lineHeight = '1.2';
      typewriterElement.style.boxDecorationBreak = 'clone';
      typewriterElement.style.webkitBoxDecorationBreak = 'clone';
      typewriterElement.textContent = '';
      cursorElement.style.color = baseColor;
      cursorElement.style.marginLeft = '0';
      cursorElement.textContent = cursorGlyph;

      const timeline = gsap.timeline({ repeat: -1, repeatDelay: 0.25 });
      tlRef.current = timeline;

      timeline.to(progress, {
        value: characterCount,
        duration: 1.7,
        ease: `steps(${Math.max(characterCount, 1)})`,
        onUpdate: () => {
          const typedCharacters = Math.max(
            0,
            Math.min(characterCount, Math.ceil(progress.value))
          );
          const typedText = previewPhraseText.slice(0, typedCharacters);
          typewriterElement.textContent = typedText;
          cursorElement.textContent = cursorGlyph;
        },
      });

      timeline.to(
        {},
        {
          duration: 2,
          onStart: () => {
            typewriterElement.textContent = previewPhraseText;
            cursorElement.textContent = cursorGlyph;
          },
          onUpdate: () => {
            typewriterElement.textContent = previewPhraseText;
            cursorElement.textContent = cursorGlyph;
          },
        }
      );

      timeline.call(() => {
        typewriterElement.textContent = '';
        cursorElement.textContent = '';
      });

      return () => {
        timeline.kill();
        gsap.killTweensOf(typewriterElement);
        gsap.killTweensOf(cursorElement);
        clearBlurPreview();
        typewriterElement.textContent = '';
        cursorElement.textContent = '';
        typewriterElement.style.backgroundColor = 'transparent';
        typewriterElement.style.padding = '0';
        typewriterElement.style.borderRadius = '0';
        typewriterElement.style.lineHeight = '';
        cursorElement.style.marginLeft = '0';
      };
    }

    const getDelayAfterWord = (index) => {
      const current = wordTimeline[index];
      const next = wordTimeline[index + 1];
      if (!current || !next) return 0.22;

      const ms = Math.max(120, Math.min(700, next.start - current.start));
      return ms / 1000;
    };

    const resetWords = () => {
      wordRefs.current.forEach((element) => {
        if (!element) return;
        gsap.killTweensOf(element);
        gsap.set(element, { clearProps: 'all', opacity: 0 });
        element.style.color = baseColor;
        element.style.backgroundColor = 'transparent';
        element.style.borderRadius = '0';
        element.style.padding = '0';
      });
    };

    resetWords();
    const timeline = gsap.timeline({ repeat: -1, repeatDelay: 0.25 });
    tlRef.current = timeline;

    const animateWordIn = (element, animationName) => {
      timeline.call(() => {
        if (!element) return;
        gsap.killTweensOf(element);
        const animate = ANIMATION_FUNCTIONS[animationName] || ANIMATION_FUNCTIONS.fadeIn;
        animate(element, false, 0, 0);
      });
    };

    if (displayMode === 'progressive') {
      allWords.forEach((_, index) => {
        const element = wordRefs.current[index];

        timeline.call(() => {
          if (activeWordBlurEnabled) {
            wordRefs.current.forEach((wordElement, wordIndex) => {
              if (!wordElement) return;
              const shouldBeVisible = wordIndex <= index;
              const isActiveWord = wordIndex === index;
              const targetOpacity = shouldBeVisible
                ? (isActiveWord ? activeWordBlurWordAlpha : 1)
                : 0;
              gsap.set(wordElement, { opacity: targetOpacity });
              wordElement.style.color = baseColor;
              wordElement.style.backgroundColor = 'transparent';
              wordElement.style.borderRadius = '0';
              wordElement.style.padding = '0';
            });
            moveActiveBlurToWord(index);
            return;
          }

          if (revealOpacityMode === 'activeTrail') {
            wordRefs.current.forEach((wordElement, wordIndex) => {
              if (!wordElement) return;
              gsap.set(wordElement, { opacity: wordIndex <= index ? 1 : inactiveOpacity });
              wordElement.style.color = baseColor;
            });
            return;
          }

          if (!highlightColor) return;
          wordRefs.current.forEach((wordElement, wordIndex) => {
            if (wordElement) {
              wordElement.style.color = wordIndex === index ? highlightColor : baseColor;
            }
          });
        });

        if (!activeWordBlurEnabled) {
          animateWordIn(element, wordAnimation);
        }
        timeline.add(() => {}, `+=${getDelayAfterWord(index)}`);
      });

      if (activeWordBlurEnabled) {
        timeline.call(() => {
          clearBlurPreview();
          wordRefs.current.forEach((wordElement) => {
            if (!wordElement) return;
            gsap.set(wordElement, { opacity: 0 });
            wordElement.style.color = baseColor;
          });
        });
      }
    } else if (displayMode === 'wordByWord') {
      allWords.forEach((_, index) => {
        const element = wordRefs.current[index];

        timeline.call(() => {
          wordRefs.current.forEach((wordElement) => {
            if (wordElement) gsap.set(wordElement, { opacity: 0 });
          });
        });

        animateWordIn(element, wordAnimation);
        timeline.add(() => {}, `+=${getDelayAfterWord(index)}`);

        if (index < allWords.length - 1) {
          timeline.to(
            element,
            { opacity: 0, duration: 0.18 },
            `+=${Math.max(0.08, getDelayAfterWord(index) * 0.35)}`
          );
        }
      });
    } else if (displayMode === 'phraseHighlight' || displayMode === 'karaoke') {
      const phraseRanges = forceSinglePhrasePreview
        ? [{ start: 0, end: allWords.length - 1 }]
        : [
          { start: 0, end: phrase1Length - 1 },
          { start: phrase1Length, end: allWords.length - 1 },
        ];

      phraseRanges.forEach((range, phraseIndex) => {
        const shouldHighlightPhrase = phraseIndex >= highlightFromPhraseIndex;
        timeline.call(() => {
          wordRefs.current.forEach((element, wordIndex) => {
            if (!element) return;

            const inPhrase = wordIndex >= range.start && wordIndex <= range.end;
            const baseOpacity = (revealOpacityMode === 'activeTrail') ? inactiveOpacity : 1;
            gsap.set(element, { opacity: inPhrase ? baseOpacity : 0 });
            element.style.color = baseColor;
            element.style.backgroundColor = 'transparent';
            element.style.borderRadius = '0';
            element.style.padding = '0';
          });
        });

        for (let index = range.start; index <= range.end; index += 1) {
          timeline.call(() => {
            if (revealOpacityMode === 'activeTrail') {
              for (let wordIndex = range.start; wordIndex <= range.end; wordIndex += 1) {
                const element = wordRefs.current[wordIndex];
                if (!element) continue;
                const shouldBeOpaque = wordIndex <= index;
                gsap.set(element, { opacity: shouldBeOpaque ? 1 : inactiveOpacity });
                element.style.color = baseColor;
                element.style.backgroundColor = 'transparent';
                element.style.borderRadius = '0';
                element.style.padding = '0';
              }
              return;
            }

            if (!highlightColor || !shouldHighlightPhrase) {
              for (let wordIndex = range.start; wordIndex <= range.end; wordIndex += 1) {
                const element = wordRefs.current[wordIndex];
                if (element) {
                  element.style.color = baseColor;
                  element.style.backgroundColor = 'transparent';
                  element.style.borderRadius = '0';
                  element.style.padding = '0';
                }
              }
              return;
            }

            for (let wordIndex = range.start; wordIndex <= range.end; wordIndex += 1) {
              const element = wordRefs.current[wordIndex];
              if (element) {
                element.style.color = wordIndex === index ? highlightColor : baseColor;
                if (highlightBackground) {
                  const isActiveWord = wordIndex === index;
                  element.style.backgroundColor = isActiveWord ? highlightBackground : 'transparent';
                  element.style.borderRadius = isActiveWord ? '3px' : '0';
                  element.style.padding = isActiveWord ? '0 3px' : '0';
                }
              }
            }
          });

          timeline.add(() => {}, `+=${getDelayAfterWord(index)}`);
        }

        if (phraseIndex < phraseRanges.length - 1) {
          timeline.add(() => {}, '+=0.12');
        }
      });
    } else if (displayMode === 'phraseStack') {
      const phraseRanges = [
        { start: 0, end: phrase1Length - 1, phraseIdx: 0 },
        { start: phrase1Length, end: allWords.length - 1, phraseIdx: 1 },
      ];

      phraseRanges.forEach((range) => {
        timeline.call(() => {
          wordRefs.current.forEach((element, wordIndex) => {
            if (!element) return;

            const wordPhraseIdx = wordIndex < phrase1Length ? 0 : 1;
            const shouldShow = wordPhraseIdx <= range.phraseIdx;
            gsap.set(element, { opacity: shouldShow ? 1 : 0 });
            element.style.color = wordPhraseIdx < range.phraseIdx ? pastColor : baseColor;
          });
        });

        for (let index = range.start; index <= range.end; index += 1) {
          animateWordIn(wordRefs.current[index], wordAnimation);
          timeline.add(() => {}, `+=${getDelayAfterWord(index)}`);
        }
      });
    } else if (displayMode === 'standard') {
      const phraseRanges = [
        { start: 0, end: phrase1Length - 1 },
        { start: phrase1Length, end: allWords.length - 1 },
      ];

      phraseRanges.forEach((range, phraseIndex) => {
        timeline.call(() => {
          wordRefs.current.forEach((element, wordIndex) => {
            if (!element) return;
            const inPhrase = wordIndex >= range.start && wordIndex <= range.end;
            gsap.set(element, { opacity: inPhrase ? 1 : 0 });
            element.style.color = baseColor;
            element.style.backgroundColor = 'transparent';
            element.style.borderRadius = '0';
            element.style.padding = '0';
          });
        });

        timeline.call(() => {
          for (let idx = range.start; idx <= range.end; idx += 1) {
            const element = wordRefs.current[idx];
            if (!element) continue;
            gsap.killTweensOf(element);
            const animate = ANIMATION_FUNCTIONS[preset.animation] || ANIMATION_FUNCTIONS.fadeIn;
            animate(element, false, 0, 0);
          }
        });

        let phraseDelay = 0;
        for (let idx = range.start; idx <= range.end; idx += 1) {
          phraseDelay += getDelayAfterWord(idx);
        }
        timeline.add(() => {}, `+=${Math.max(0.3, phraseDelay)}`);

        if (phraseIndex < phraseRanges.length - 1) {
          timeline.add(() => {}, '+=0.08');
        }
      });
    } else {
      timeline.call(() => {
        wordRefs.current.forEach((element) => {
          if (element) {
            gsap.set(element, { opacity: 1 });
            element.style.color = baseColor;
          }
        });
      });

      allWords.forEach((_, index) => {
        timeline.call(() => {
          wordRefs.current.forEach((element, wordIndex) => {
            if (!element) return;

            if (highlightColor) {
              element.style.color = wordIndex === index ? highlightColor : baseColor;
            }

            if (highlightBackground) {
              element.style.backgroundColor = wordIndex === index ? highlightBackground : 'transparent';
              element.style.borderRadius = wordIndex === index ? '3px' : '0';
              element.style.padding = wordIndex === index ? '0 3px' : '0';
            }
          });
        });

        timeline.add(() => {}, `+=${getDelayAfterWord(index)}`);
      });
    }

    const refsSnapshot = [...wordRefs.current];
    return () => {
      timeline.kill();
      clearBlurPreview();
      refsSnapshot.forEach((element) => {
        if (element) gsap.killTweensOf(element);
      });
    };
  }, [isInView, preset, allWords, previewPhraseText, isTypewriterPreset, phrase1Length, wordTimeline]);

  const previewColor = preset.color || '#ffffff';
  const previewBackground = preset.bg || '#1a1a1a';
  const hasStroke = preset.stroke && preset.strokeThickness;
  const hasGlow = false;

  const strokeCSS = hasStroke
    ? `${Math.min(preset.strokeThickness / 2, 2)}px ${preset.stroke}`
    : undefined;
  const shadowCSS = hasGlow
    ? `0 0 ${preset.dropShadowBlur || 8}px ${preset.dropShadowColor}`
    : hasStroke
      ? `1px 1px 0px ${preset.stroke}, -1px -1px 0px ${preset.stroke}, 1px -1px 0px ${preset.stroke}, -1px 1px 0px ${preset.stroke}`
      : 'none';

  const wordStyle = {
    color: previewColor,
    textShadow: shadowCSS,
    WebkitTextStroke: strokeCSS,
    paintOrder: 'stroke fill',
    display: 'inline-block',
    transition: 'background-color 0.15s ease',
  };

  return (
    <button onClick={() => updateStyle(preset)} className="flex flex-col items-center group w-full">
      <div
        ref={containerRef}
        className="relative w-full h-24 rounded-lg flex flex-col items-center justify-center border border-[#333] group-hover:border-[#d4f391] transition-colors overflow-hidden"
        style={{ backgroundColor: previewBackground }}
      >
        <div
          ref={blurLayerRef}
          className="pointer-events-none absolute inset-0 z-20"
        >
          <span
            ref={activeBlurRef}
            style={{
              position: 'absolute',
              display: 'block',
              width: 0,
              height: 0,
              opacity: 0,
              borderRadius: '999px',
              transform: 'translate(-9999px, -9999px)',
            }}
          />
        </div>

        <div
          ref={previewTextRef}
          className="relative z-10 text-center px-2 leading-[1.1] inline-block rounded-md"
          style={{
            fontFamily: preset.fontFamily || 'Inter',
            fontSize: '12px',
            fontWeight: '800',
            backgroundColor: preset.backgroundColor || 'transparent',
            padding: preset.backgroundColor ? '2px 8px' : '0px',
          }}
        >
          {isTypewriterPreset ? (
            <span>
              <span
                ref={typewriterTextRef}
                style={{
                  ...wordStyle,
                  whiteSpace: 'pre-wrap',
                  display: 'inline',
                  boxDecorationBreak: 'clone',
                  WebkitBoxDecorationBreak: 'clone',
                }}
              />
              <span
                ref={typewriterCursorRef}
                style={{
                  ...wordStyle,
                  display: 'inline',
                  backgroundColor: 'transparent',
                  padding: '0',
                }}
              />
            </span>
          ) : (
            allWords.map((word, index) => (
              <span
                key={index}
                ref={(element) => {
                  wordRefs.current[index] = element;
                }}
                style={wordStyle}
              >
                {word}
                {index < allWords.length - 1 ? ' ' : ''}
              </span>
            ))
          )}
        </div>
      </div>
      <div className="mt-1.5 text-gray-400 text-[9px] uppercase tracking-wider font-medium">{preset.label}</div>
    </button>
  );
};

export default CaptionPresetButton;
